import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, FileText, CheckCircle, 
  AlertTriangle, Loader2, Lock, Shield, Award,
  Zap, Star, TrendingUp, FileCheck, Clock,
  MessageSquare, ListChecks
} from 'lucide-react';
import { LogoFull, LogoMark } from '../components/LogoFull';
import { JarvisLoader } from '../components/JarvisLoader';
import { useLanguage } from '../context/LanguageContext';
import { getReport, createCheckout } from '../lib/api';

const ScoreGauge = ({ score, label }) => {
  const getColor = () => {
    if (score >= 80) return '#22c55e';
    if (score >= 65) return '#C9A24A';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const color = getColor();
  const circumference = 2 * Math.PI * 54;
  const progress = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center" data-testid="score-gauge">
      <div className="relative w-40 h-40">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
          <motion.circle
            cx="60" cy="60" r="54" fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: circumference - progress }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            className="text-4xl font-bold"
            style={{ color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {score}
          </motion.span>
          <span className="text-xs text-gray-500 uppercase tracking-wider">de 100</span>
        </div>
      </div>
      <motion.div
        className="mt-3 px-4 py-1.5 rounded-full text-sm font-semibold"
        style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        {label}
      </motion.div>
    </div>
  );
};

export default function PreviewPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const data = await getReport(caseId);
        setCaseData(data);
      } catch (err) {
        console.error('Error fetching case:', err);
        setError('Caso nao encontrado');
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchCase();
    }
  }, [caseId]);

  const handleCheckout = async (plan) => {
    if (processingPayment) return;
    
    setProcessingPayment(true);
    setSelectedPlan(plan);
    setShowLoader(true);
    
    try {
      const result = await createCheckout(caseId, window.location.origin, plan);
      setTimeout(() => {
        window.location.href = result.checkout_url;
      }, 2000);
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Erro ao processar pagamento. Tente novamente.');
      setProcessingPayment(false);
      setShowLoader(false);
      setSelectedPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000510] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00D4FF] animate-spin" />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen bg-[#000510] flex items-center justify-center px-6">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl text-white mb-2">Caso nao localizado</h1>
          <p className="text-gray-400 mb-6">{error || 'O caso solicitado nao existe.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-[#00D4FF] text-[#000510] font-semibold uppercase tracking-wider text-sm"
            data-testid="return-btn"
          >
            Retornar
          </button>
        </div>
      </div>
    );
  }

  if (caseData.status === 'generated' || caseData.status === 'emailed') {
    navigate(`/report/${caseId}`);
    return null;
  }

  const score = caseData.score || 50;
  const scoreLabel = caseData.score_label || 'Moderado';
  const destination = caseData.destination_country || 'Espanha';

  return (
    <div className="min-h-screen bg-[#000510]">
      <JarvisLoader 
        isLoading={showLoader} 
        language={language}
        clientName={caseData.nome}
      />

      {/* Header */}
      <header className="px-6 py-6 border-b border-[#00D4FF]/10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <LogoFull size="small" />
          <span className="text-sm text-gray-600 font-mono">
            ID: {caseId}
          </span>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Score Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#000815] border border-[#00D4FF]/20 p-8 mb-8 text-center"
            data-testid="score-section"
          >
            <div className="mb-2">
              <span className="text-[#00D4FF] text-xs uppercase tracking-widest font-medium">
                Score de Visto para {destination}
              </span>
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6">
              {caseData.nome}, sua pontuacao de aprovacao:
            </h2>
            
            <ScoreGauge score={score} label={scoreLabel} />

            <p className="text-gray-400 text-sm mt-6 max-w-lg mx-auto">
              {score >= 70 
                ? `${caseData.nome}, seu score de ${score} indica boas chances. Mas voce pode ser negado por erros na documentacao. Desbloqueie seu Plano de Acao para garantir a aprovacao.`
                : score >= 50
                ? `${caseData.nome}, seu score de ${score} e considerado ${scoreLabel}. Voce tem pontos de atencao que podem causar uma negativa imediata. Desbloqueie seu Plano de Acao para saber como subir esse score para +80.`
                : `${caseData.nome}, seu score de ${score} exige atencao. Ha desafios no seu caso, mas existem caminhos possiveis. Desbloqueie o roteiro juridico para aumentar suas chances de aprovacao.`
              }
            </p>
          </motion.div>

          {/* Preview Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#000815] border border-[#00D4FF]/10 p-8 mb-8"
          >
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#00D4FF]/10">
              <FileText className="w-5 h-5 text-[#00D4FF]" />
              <h2 className="font-['Playfair_Display'] text-xl text-white">
                Avaliacao Preliminar
              </h2>
            </div>
            
            <div className="text-gray-300 leading-relaxed space-y-4">
              {caseData.preview_text?.split('\n').map((line, i) => {
                const cleanLine = line
                  .replace(/\*\*/g, '')
                  .replace(/^\d+\)\s*/, '')
                  .replace(/^-\s*/, '')
                  .trim();
                
                if (!cleanLine) return null;
                return <p key={i}>{cleanLine}</p>;
              })}
            </div>

            {/* Premium Content Teaser */}
            <div className="mt-8 relative">
              <div className="absolute inset-0 bg-gradient-to-t from-[#000510] via-[#000510]/90 to-transparent z-10" />
              <div className="blur-sm text-gray-600 text-sm space-y-2">
                <p className="font-semibold">O Relatorio Completo inclui:</p>
                <ul className="space-y-1 ml-4">
                  <li>- Analise detalhada do perfil migratorio</li>
                  <li>- Rota principal e alternativa estrategica</li>
                  <li>- Checklist documental completo</li>
                  <li>- Cronograma de acoes</li>
                  <li>- Estimativa de investimento</li>
                </ul>
              </div>
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="bg-[#000510]/80 p-4 border border-[#00D4FF]/20">
                  <Lock className="w-6 h-6 text-[#00D4FF] mx-auto" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <h3 className="text-center font-['Playfair_Display'] text-2xl text-white mb-2">
              Nao corra riscos desnecessarios
            </h3>
            <p className="text-center text-gray-500 text-sm mb-8">
              Desbloqueie agora seu roteiro juridico para {destination}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Basic Plan - R$49.99 */}
              <div 
                className="bg-[#000815] border border-[#00D4FF]/20 p-8 relative"
                data-testid="plan-basic"
              >
                <div className="text-center mb-6">
                  <h4 className="font-['Playfair_Display'] text-xl text-white mb-1">
                    Relatorio Essencial
                  </h4>
                  <p className="text-gray-500 text-sm">Visto + Checklist Completo</p>
                </div>

                <div className="text-center py-4 mb-6 border-y border-[#00D4FF]/10">
                  <span className="text-gray-500 text-sm block mb-1">Investimento unico</span>
                  <span className="text-4xl font-['Playfair_Display'] text-white">R$49,90</span>
                  <span className="text-[#00D4FF] text-xs block mt-1">ou 3x de R$16,63</span>
                </div>

                <div className="space-y-3 mb-8">
                  {[
                    { icon: TrendingUp, text: 'Resumo executivo do caso' },
                    { icon: FileText, text: 'Rotas legais recomendadas' },
                    { icon: ListChecks, text: 'Checklist de documentos' },
                    { icon: Clock, text: 'Cronograma de acoes (7/14/30 dias)' },
                    { icon: Shield, text: 'Analise de riscos' },
                    { icon: FileCheck, text: 'PDF profissional' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <item.icon className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />
                      <span className="text-gray-400">{item.text}</span>
                    </div>
                  ))}
                </div>

                <button
                  data-testid="checkout-basic-btn"
                  onClick={() => handleCheckout('basic')}
                  disabled={processingPayment}
                  className={`w-full py-4 bg-[#00D4FF] text-[#000510] font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-3 hover:bg-white transition-colors ${
                    processingPayment && selectedPlan === 'basic' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {processingPayment && selectedPlan === 'basic' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processando...</>
                  ) : (
                    <><CreditCard className="w-5 h-5" /> Desbloquear Relatorio Essencial</>
                  )}
                </button>
              </div>

              {/* Premium Plan - R$149,90 */}
              <div 
                className="bg-[#000815] border-2 border-[#C9A24A]/50 p-8 relative"
                data-testid="plan-premium"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#C9A24A] text-[#050608] text-xs font-bold uppercase tracking-wider">
                  Mais Escolhido
                </div>

                <div className="text-center mb-6">
                  <h4 className="font-['Playfair_Display'] text-xl text-white mb-1">
                    Plano VIP
                  </h4>
                  <p className="text-gray-500 text-sm">Relatorio + Suporte WhatsApp</p>
                </div>

                <div className="text-center py-4 mb-6 border-y border-[#C9A24A]/20">
                  <span className="text-gray-500 text-sm block mb-1">Investimento unico</span>
                  <span className="text-4xl font-['Playfair_Display'] text-transparent bg-clip-text bg-gradient-to-r from-[#C9A24A] to-[#E0B75B]">
                    R$149,90
                  </span>
                  <span className="text-[#C9A24A] text-xs block mt-1">ou 3x de R$49,97</span>
                </div>

                <div className="space-y-3 mb-8">
                  {[
                    { icon: TrendingUp, text: 'Tudo do Relatorio Essencial', gold: false },
                    { icon: MessageSquare, text: 'Dicas para entrevista consular', gold: true },
                    { icon: ListChecks, text: 'Checklist detalhado passo a passo', gold: true },
                    { icon: Star, text: 'Estrategia de aprovacao personalizada', gold: true },
                    { icon: Zap, text: 'Onde obter cada documento', gold: true },
                    { icon: Award, text: 'Suporte direto via WhatsApp com especialista', gold: false },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <item.icon className={`w-4 h-4 flex-shrink-0 ${item.gold ? 'text-[#C9A24A]' : 'text-[#00D4FF]'}`} />
                      <span className={item.gold ? 'text-gray-200 font-medium' : 'text-gray-400'}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  data-testid="checkout-premium-btn"
                  onClick={() => handleCheckout('premium')}
                  disabled={processingPayment}
                  className={`w-full py-4 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all ${
                    processingPayment && selectedPlan === 'premium' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {processingPayment && selectedPlan === 'premium' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processando...</>
                  ) : (
                    <><CreditCard className="w-5 h-5" /> Quero o Plano VIP</>
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Security Badge + Guarantee */}
          <div className="flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/20 px-6 py-3 rounded-lg">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-green-300 text-sm font-medium">
                Garantia de 7 dias - 100% do dinheiro de volta se nao ficar satisfeito
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                <span>Pagamento seguro via Stripe</span>
              </div>
              <span>Parcele em ate 3x sem juros</span>
            </div>
          </div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12 flex items-start gap-4 text-xs text-gray-600"
          >
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              O FORTUNATO LEGISLAI utiliza inteligencia artificial para buscar e analisar em tempo real
              informacoes de fontes oficiais de imigracao. Este servico nao constitui aconselhamento juridico formal.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
